import { nextTick, ref, watch } from "vue";
import i18next from "i18next";

import { Facade } from "@/drawing";
import { Logger } from "@/logger";
import { MemoryMonitor } from "@/utils/MemoryMonitor";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { activityEventRepository } from "@/repositories/activityEvent.repository";
import { db } from "@/repositories/database";
import { getDeviceOS } from "@/utils/device-fingerprint";
import CanvasSettingsState from "./CanvasSettingsState";
import { trackAction } from "@/analytics/drawing-analytics";
import { clearPointers, isUserInteracting } from "./CanvasViewState";
import Settings from "./Settings";
import { editingLocked } from "./EditingLockState";

export const SaveState = ref<"saved" | "saving" | "changed">("saved");

/** Whether the current save is automatic or manual (for overlay message) */
export const SaveType = ref<"auto" | "manual" | null>(null);

/** Délai d'inactivité avant une sauvegarde automatique. */
export const AUTOSAVE_DELAY_MS = 30 * 1000;

/**
 * Retard au-delà duquel une échéance atteinte n'est plus interprétée comme une
 * vraie inactivité mais comme un timer **gelé** qui vient de repartir (cf.
 * `runAutosave`). Filet indépendant des events de visibilité : certains
 * navigateurs mobiles n'émettent ni `visibilitychange` ni `pagehide` au
 * verrouillage de l'écran.
 */
export const OVERDUE_TOLERANCE_MS = 5 * 1000;

/**
 * Au-delà de ce seuil, une sauvegarde est considérée trop lente pour avoir une
 * chance d'aboutir pendant une mise en arrière-plan (cf. `canSaveInBackground`).
 */
const BACKGROUND_SAVE_MAX_DURATION_MS = 5 * 1000;

/**
 * Durée de la dernière sauvegarde (sérialisation ODA + écriture IndexedDB).
 * `null` tant qu'aucune sauvegarde n'a eu lieu dans cette session.
 */
export const lastSaveDurationMs = ref<number | null>(null);

// Controller state (module-level, not tied to component lifecycle)
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
let autosaveDeadline = 0;
let onEditHandler: (() => void) | null = null;
let onVisibilityChange: (() => void) | null = null;
let onPageHide: (() => void) | null = null;
let stopAutoSaveSettingWatch: (() => void) | null = null;
let stopInteractionWatch: (() => void) | null = null;
let isHidden = false;
let isControllerInitialized = false;

/**
 * Incrémenté à chaque modification. Comparé avant/après une sauvegarde pour
 * détecter une modification concurrente (raccourci clavier pendant l'overlay) et
 * ne pas écraser le drapeau "changed" qu'elle vient de poser.
 */
let editSeq = 0;

/**
 * Échecs consécutifs de sauvegarde, remis à zéro au premier succès. Sert à
 * n'afficher qu'une `alert()` par série, et remonté à Sentry pour distinguer un
 * incident isolé d'un appareil durablement bloqué.
 */
let consecutiveFailures = 0;

/**
 * Wait for Vue to render and the browser to paint.
 * This ensures the overlay is visible before blocking operations.
 *
 * No-op quand la page est cachée : il n'y a aucun paint à attendre, et les
 * timers y sont throttlés (≥ 1 s sur Chrome) — ce qui grignoterait le peu de
 * temps d'exécution accordé à une sauvegarde d'arrière-plan avant suspension.
 */
function waitForRender(): Promise<void> {
  if (typeof document !== "undefined" && document.visibilityState !== "visible")
    return Promise.resolve();
  return new Promise((resolve) => {
    nextTick(() => {
      // setTimeout(0) yields to browser render cycle
      setTimeout(resolve, 16); // ~1 frame at 60fps
    });
  });
}

/**
 * Occupation du stockage au moment d'un échec — n'est lu que dans le chemin
 * d'erreur (coût nul en fonctionnement normal). Donne des octets exacts, là où
 * le `percentageUsed` de `useStorageQuota` n'est rafraîchi que tant qu'un
 * composant l'ayant monté est vivant — ce qui n'est pas le cas sur le canvas.
 * Ne doit jamais masquer l'erreur d'origine, d'où le try/catch.
 */
async function getStorageEstimate() {
  try {
    if (!navigator.storage?.estimate) return null;
    // Borné : sur un stockage en souffrance, l'estimation elle-même peut traîner,
    // et l'overlay bloquant attend la fin du chemin d'erreur pour disparaître.
    const { usage, quota } = await withTimeout(
      navigator.storage.estimate(),
      1_000,
      "estimate",
    );
    return { usage, quota };
  } catch {
    return null;
  }
}

/**
 * Délais maximaux des étapes ASYNCHRONES d'une sauvegarde. Sans eux, une écriture
 * IndexedDB qui n'aboutit jamais (base verrouillée par une autre instance, disque
 * saturé…) laissait l'overlay bloquant affiché indéfiniment : l'utilisateur
 * n'avait plus d'autre issue que de tuer l'application en pleine transaction.
 * La sérialisation par le moteur WASM, elle, est synchrone et ne peut pas être
 * interrompue.
 */
export const SAVE_STEP_TIMEOUTS_MS = {
  readBuffer: 15_000,
  writeDraft: 30_000,
  markSaved: 15_000,
} as const;

/** Une étape de sauvegarde a dépassé son délai (cf. SAVE_STEP_TIMEOUTS_MS). */
export class SaveTimeoutError extends Error {
  constructor(
    public readonly step: string,
    public readonly timeoutMs: number,
  ) {
    super(`Save step "${step}" did not complete within ${timeoutMs}ms`);
    this.name = "SaveTimeoutError";
  }
}

/**
 * Rejette si `promise` ne s'est pas réglée dans le délai. La promesse d'origine
 * continue sa course : une écriture IndexedDB qui aboutit plus tard est
 * simplement rattrapée par la sauvegarde suivante.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  step: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new SaveTimeoutError(step, ms)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Écriture en base encore en cours, même après le dépassement de son délai : la
 * transaction IndexedDB ne s'annule pas, et elle retient le dessin sérialisé
 * (plusieurs Mo). Tant qu'elle n'est pas réglée, aucune nouvelle sauvegarde ne
 * démarre — sinon chaque tentative empilerait une copie de plus en mémoire
 * jusqu'au crash de l'onglet. Le délai sert à rendre la main à l'utilisateur, pas
 * à autoriser des écritures concurrentes.
 */
let pendingWrite: Promise<unknown> | null = null;

function trackPendingWrite<T>(write: Promise<T>): Promise<T> {
  pendingWrite = write;
  write
    .finally(() => {
      if (pendingWrite === write) pendingWrite = null;
    })
    .catch(() => {
      // l'erreur est traitée par l'appelant
    });
  return write;
}

/**
 * Core save function. Can be used independently.
 * @param isAuto - Whether this is an automatic save (for overlay message)
 */
export async function saveDrawing(isAuto = true) {
  // Don't start a new save if already saving
  if (SaveState.value === "saving") return;
  // Une écriture qui a dépassé son délai est toujours en file : on n'en ajoute
  // pas une autre (cf. pendingWrite). L'état reste « changed », la prochaine
  // échéance réessaiera.
  if (pendingWrite) {
    Logger.warn(
      "saveDrawing: previous draft write still pending, save skipped",
    );
    return;
  }

  const wasDirty = SaveState.value === "changed";
  const editSeqAtStart = editSeq;
  SaveState.value = "saving";
  SaveType.value = isAuto ? "auto" : "manual";

  // Wait for Vue to render the overlay before blocking with the save operation
  await waitForRender();

  const startedAt = performance.now();
  let didWrite = false;
  let byteLength: number | null = null;
  try {
    const drawingId = CanvasSettingsState.drawingId;
    if (drawingId && Facade.isInitialized && Facade.file.isOpen()) {
      // La sérialisation (Blob + ArrayBuffer) double transitoirement la mémoire
      // pendant que la scène WASM live reste résidente — attendu à chaque save,
      // pas un leak. Fenêtre large pour couvrir un device lent avant que le GC
      // ne repasse.
      MemoryMonitor.suppressReportingFor(20_000);
      const blob = await Facade.file.save(`drawing_${drawingId}.dwg`);
      const arrayBuffer = await withTimeout(
        blob.arrayBuffer(),
        SAVE_STEP_TIMEOUTS_MS.readBuffer,
        "read-buffer",
      );
      byteLength = arrayBuffer.byteLength;
      if (arrayBuffer.byteLength === 0) {
        throw new Error(
          "Serialized drawing is 0 bytes — save aborted to protect existing data",
        );
      }
      await withTimeout(
        trackPendingWrite(
          localDrawingRepository.saveDraft(drawingId, arrayBuffer),
        ),
        SAVE_STEP_TIMEOUTS_MS.writeDraft,
        "write-draft",
      );
      // Oplog : le brouillon local inclut désormais les events encore pending —
      // ils survivraient à une fermeture d'onglet, distinct d'un push backend.
      await withTimeout(
        trackPendingWrite(activityEventRepository.markLocallySaved(drawingId)),
        SAVE_STEP_TIMEOUTS_MS.markSaved,
        "mark-saved",
      );
      didWrite = true;
      consecutiveFailures = 0;
      // Mesure du coût réel : elle pilote `canSaveInBackground()`, donc on ne la
      // renseigne que sur une sauvegarde réellement effectuée (pas sur un no-op
      // ni sur une erreur, qui sortirait typiquement en avance).
      lastSaveDurationMs.value = performance.now() - startedAt;
      Logger.info(
        `[${isAuto ? "AutoSave" : "ManualSave"}] Draft saved (${
          arrayBuffer.byteLength
        } bytes) in ${Math.round(lastSaveDurationMs.value)}ms`,
      );
    }
  } catch (e) {
    Logger.error(`saveDrawing: error while saving drawing: ${e}`);
    const isQuotaExceeded =
      e instanceof DOMException && e.name === "QuotaExceededError";
    consecutiveFailures++;
    const isPageVisible =
      typeof document === "undefined" || document.visibilityState === "visible";

    // Perdre un dessin sans trace est le pire scénario du produit : `Logger.error`
    // n'écrit que dans la console (cf. logger.ts), il faut une vraie remontée.
    Logger.captureException(e, {
      drawingId: CanvasSettingsState.drawingId,
      isAuto,
      isQuotaExceeded,
      pageVisible: isPageVisible,
      consecutiveFailures,
      durationMs: Math.round(performance.now() - startedAt),
      byteLength,
      storage: await getStorageEstimate(),
    });

    // Une seule alerte par série d'échecs, et jamais en arrière-plan : sur un
    // stockage saturé, chaque tentative échouerait et l'utilisateur serait
    // accueilli par une pile d'`alert()` bloquantes au réveil.
    if (isPageVisible && consecutiveFailures === 1) {
      alert(
        i18next.t(
          isQuotaExceeded
            ? "canvas:save.quotaExceededError"
            : "canvas:save.error",
        ),
      );
    }
  } finally {
    // "saved" seulement s'il n'y a effectivement plus rien à sauvegarder : une
    // écriture ratée, ou une modification arrivée pendant l'écriture, doivent
    // laisser le badge rouge plutôt que d'affirmer un dessin sauvegardé qui ne
    // l'est pas.
    const editedDuringSave = editSeq !== editSeqAtStart;
    SaveState.value =
      (didWrite || !wasDirty) && !editedDuringSave ? "saved" : "changed";
    SaveType.value = null;
  }
}

/**
 * Delays (ms) between retries of the unsaved-oplog purge. `bindPendingToRevision`
 * now only binds `localSaved: 1` events, so a failed purge can no longer leak
 * into the NEXT sync — the residual risk is a MUCH later one (the same drawing
 * reopened and saved again promotes leftover `localSaved: 0` events via
 * `markLocallySaved`). Retrying is only worth it for a genuinely transient
 * failure (momentary contention on that one record) — cf. `purgeUnsavedOplogWithRetry`,
 * which skips straight to reporting if `db.isOpen()` says the whole connection
 * is gone, since retrying on a dead connection would just fail identically
 * every time (cf. DR-DRAWING-MR / `database.ts`'s `on("close")` handler).
 */
const DISCARD_PURGE_RETRY_DELAYS_MS = [500, 2000, 5000];

/**
 * Dernier discard déclenché (cf. `discardUnsavedChanges`) — permet à
 * `DrawingCanvas.vue` d'attendre la fin de la purge (retries compris) avant de
 * déclencher un sync sur le même dessin (`onBeforeUnmount` → `triggerSync`).
 * Sans ça, la purge (fire-and-forget, appelée depuis `CanvasExitDialog.vue`/`router.ts`
 * juste avant la navigation) et le sync qui suit quasi immédiatement peuvent
 * s'exécuter dans un ordre non garanti — Dexie diffère l'exécution réelle de
 * `.delete()` à un microtask, et la navigation Vue Router est elle aussi
 * asynchrone, donc l'ordre d'appel dans le code source ne garantit rien.
 */
let pendingDiscardPurge: Promise<void> | null = null;

async function purgeUnsavedOplogWithRetry(drawingId: number): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await activityEventRepository.clearUnsavedByDrawing(drawingId);
      return;
    } catch (e: any) {
      const connectionClosed = !db.isOpen();
      const retriesExhausted = attempt >= DISCARD_PURGE_RETRY_DELAYS_MS.length;
      if (connectionClosed || retriesExhausted) {
        Logger.captureException(
          new Error(
            `discardUnsavedChanges() : failed to purge unsaved oplog for drawing ${drawingId} after ${
              attempt + 1
            } attempt(s)${
              connectionClosed ? " (db connection closed, gave up early)" : ""
            } - ${e?.message}`,
          ),
        );
        return;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, DISCARD_PURGE_RETRY_DELAYS_MS[attempt]),
      );
    }
  }
}

/**
 * Abandonne les modifications non sauvegardées du dessin courant (dialogues
 * "Abandonner" / navigation confirmée hors du canvas — `CanvasExitDialog.vue`, `router.ts`).
 * Purge uniquement les events oplog jamais sauvegardés, ni localement ni côté
 * backend (`clearUnsavedByDrawing`) — PAS tous les events "pending" : un brouillon
 * déjà sauvegardé localement lors d'une session offline antérieure doit survivre,
 * il sera légitimement poussé au prochain sync.
 */
export function discardUnsavedChanges(): void {
  SaveState.value = "saved";
  const drawingId = CanvasSettingsState.drawingId;
  if (drawingId) {
    pendingDiscardPurge = purgeUnsavedOplogWithRetry(drawingId);
  }
}

/**
 * À appeler avant de déclencher un sync pour le dessin qui vient (peut-être)
 * d'être abandonné — cf. `pendingDiscardPurge`. No-op si aucun discard n'est en
 * cours.
 */
export function waitForPendingDiscard(): Promise<void> {
  return pendingDiscardPurge ?? Promise.resolve();
}

/**
 * Planifie une sauvegarde automatique dans `delay` ms, en remplaçant toute
 * échéance déjà armée.
 *
 * Volontairement un `setTimeout` explicite plutôt qu'un `lodash.debounce` : un
 * debounce ne laisse aucune prise sur les transitions de visibilité. Quand
 * l'écran se verrouille, le navigateur gèle/throttle les timers — l'échéance ne
 * se déclenche pas, elle devient simplement *en retard*, et lodash l'invoque
 * immédiatement au réveil. C'est exactement le gel de 10-15 s subi par
 * l'utilisateur au moment où il rouvre l'app pour redessiner.
 */
export function scheduleAutosave(delay: number = AUTOSAVE_DELAY_MS) {
  cancelAutosave();
  if (!Settings.autoSaveOn) return;
  autosaveDeadline = Date.now() + delay;
  autosaveTimer = setTimeout(runAutosave, delay);
}

export function cancelAutosave() {
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
}

/**
 * Échéance atteinte : on ne sauvegarde que si le moment est vraiment opportun.
 * Un garde-fou qui échoue **replanifie** plutôt que d'abandonner, sauf ceux pour
 * lesquels un autre chemin réarmera de toute façon (rien à sauvegarder, onglet
 * caché → `handleVisible`, réglage désactivé → watch du réglage).
 */
function runAutosave() {
  autosaveTimer = null;

  if (!Settings.autoSaveOn) return;
  if (SaveState.value !== "changed") return;
  // Fusion de versions en cours : une sauvegarde créerait une révision qui la
  // ferait échouer (tête déplacée). On réessaie plus tard.
  if (editingLocked.value) {
    scheduleAutosave();
    return;
  }
  // Caché : `handleVisible` réarmera un délai complet au retour.
  if (typeof document !== "undefined" && document.visibilityState !== "visible")
    return;

  // Échéance très en retard = le timer a été gelé (écran verrouillé, onglet en
  // arrière-plan) et vient seulement de repartir. Sauvegarder maintenant, c'est
  // geler le canvas à l'instant précis où l'utilisateur le rouvre pour
  // redessiner : on réarme un délai complet à la place.
  if (Date.now() - autosaveDeadline > OVERDUE_TOLERANCE_MS) {
    Logger.info(
      "[SaveController] Overdue autosave (frozen timer) → rescheduled",
    );
    scheduleAutosave();
    return;
  }

  // Dernier rempart contre la course d'ordonnancement : `pointerdown` et le
  // callback du timer sont deux macrotâches, et une échéance déjà échue peut être
  // exécutée AVANT la distribution du pointerdown, même si le doigt s'est posé en
  // premier. Couvre aussi `handleVisible`, qui planifie sans regarder le contact.
  // Pas de replanification ici : le relâchement du dernier pointeur s'en charge.
  if (isUserInteracting.value) return;

  void saveDrawing(true);
}

/**
 * Une sauvegarde lancée au passage en arrière-plan a-t-elle une chance
 * d'aboutir avant que l'OS ne suspende la page ?
 *
 * - Android / desktop : le renderer continue de tourner en arrière-plan, et le
 *   throttling ne concerne que la *planification* des timers, pas une tâche déjà
 *   démarrée — une sérialisation lancée synchroniquement va généralement au bout.
 * - iOS : le process est suspendu 1-2 s après le passage en arrière-plan. Une
 *   sérialisation longue serait coupée en plein milieu et reprendrait au réveil,
 *   c'est-à-dire qu'on reproduirait le bug qu'on corrige.
 *
 * D'où la règle adaptative : si on a déjà mesuré une sauvegarde, on se fie à sa
 * durée ; sinon on retombe sur la plateforme.
 */
function canSaveInBackground(): boolean {
  if (lastSaveDurationMs.value === null) return getDeviceOS() !== "iOS";
  return lastSaveDurationMs.value <= BACKGROUND_SAVE_MAX_DURATION_MS;
}

/**
 * Passage en arrière-plan (verrouillage d'écran, changement d'app/onglet).
 * On annule systématiquement l'échéance en cours — c'est ce qui garantit
 * qu'aucune sauvegarde héritée ne se déclenchera au réveil — puis on tente une
 * sauvegarde immédiate quand elle a des chances d'aboutir : l'utilisateur ne
 * regarde pas, l'overlay ne coûte rien.
 */
function handleHidden() {
  if (isHidden) return;
  isHidden = true;

  // Purge AVANT l'annulation : vider les pointeurs fait passer
  // `isUserInteracting` à false, ce qui réarme une échéance via le watch — que le
  // `cancelAutosave()` qui suit doit donc balayer. L'ordre inverse laisserait un
  // timer courir en arrière-plan.
  clearPointers();
  cancelAutosave();

  if (
    Settings.autoSaveOn &&
    SaveState.value === "changed" &&
    canSaveInBackground()
  ) {
    Logger.info(
      "[SaveController] Backgrounded with changes → best-effort save",
    );
    void saveDrawing(true);
  }
}

/**
 * Retour au premier plan. Ne déclenche JAMAIS de sauvegarde : il réarme un délai
 * complet, de sorte que l'utilisateur retrouve un canvas immédiatement utilisable.
 */
function handleVisible() {
  if (!isHidden) return;
  isHidden = false;

  if (SaveState.value === "changed") {
    scheduleAutosave();
  }
}

/**
 * Manually trigger a save (called when user clicks the save badge).
 * Cancels any pending auto-save and saves immediately.
 */
export function manualSave() {
  // Tracked here rather than in saveDrawing(): that one also runs the 60s
  // auto-save, which would swamp every other action in the charts.
  trackAction("manual_save");
  // Cancel pending auto-save
  cancelAutosave();
  // Save immediately with manual flag
  saveDrawing(false);
}

/**
 * Initialize the save controller.
 * Call this once in DrawingCanvas.vue AFTER the file is opened.
 */
export function initSaveController() {
  if (isControllerInitialized) {
    Logger.warn("[SaveController] Already initialized, skipping");
    return;
  }

  isHidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";

  // Store handler reference for cleanup
  onEditHandler = () => {
    editSeq++;
    SaveState.value = "changed";
    // Doigt encore posé : le relâchement du dernier pointeur réarmera.
    if (!isUserInteracting.value) scheduleAutosave();
  };

  // Subscribe directly to history changes (file is already open at this point)
  Facade.history.onChange(onEditHandler);

  // L'inactivité se mesure sur l'INTERACTION, pas sur les events d'historique :
  // ceux-ci ne sont émis qu'au commit d'une commande, donc un panoramique ou un
  // zoom prolongé — doigt bien posé sur l'écran — ne repousserait jamais
  // l'échéance. Le contact annule le timer, le relâchement le réarme : une
  // sauvegarde ne peut donc structurellement plus surgir pendant un geste.
  //
  // `isUserInteracting` (cf. `CanvasViewState`) est multitouch-exact et ne bascule
  // qu'au premier appui et au relâchement du DERNIER pointeur — lever un doigt sur
  // un pincement ne termine pas le geste. Il couvre toute l'app, pas seulement le
  // canvas : quelqu'un qui manipule un menu est actif et ne doit pas se prendre un
  // gel de 10-15 s.
  stopInteractionWatch = watch(
    isUserInteracting,
    (interacting) => {
      if (interacting) {
        cancelAutosave();
      } else if (SaveState.value === "changed") {
        scheduleAutosave();
      }
    },
    // Flush synchrone impératif : sur le flush "pre" par défaut, l'annulation
    // s'exécuterait dans une microtâche, donc APRÈS un `pointerup` émis dans le
    // même tour de boucle — elle annulerait l'échéance que le relâchement vient
    // de réarmer, et l'auto-save serait mort. Même raison que dans
    // `useSaveReminder.ts` : on arme un timer, on ne touche pas au DOM.
    { flush: "sync" },
  );

  onVisibilityChange = () => {
    if (document.visibilityState === "hidden") handleHidden();
    else handleVisible();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  // Certains cas iOS n'émettent que `pagehide` — `handleHidden` est idempotent
  // (garde `isHidden`), les deux sources peuvent donc coexister sans doublon.
  onPageHide = () => handleHidden();
  window.addEventListener("pagehide", onPageHide);

  // Le panneau de réglages n'est pas atteignable depuis le canvas aujourd'hui,
  // mais garder les deux états cohérents évite un timer orphelin si ça change.
  stopAutoSaveSettingWatch = watch(
    () => Settings.autoSaveOn,
    (enabled) => {
      if (enabled) {
        if (SaveState.value === "changed") scheduleAutosave();
      } else {
        cancelAutosave();
      }
    },
  );

  isControllerInitialized = true;

  Logger.info("[SaveController] Initialized");
}

/**
 * Cleanup the save controller.
 * Call this in DrawingCanvas.vue onBeforeUnmount, BEFORE the final save.
 */
export function cleanupSaveController() {
  // Cancel pending autosave
  cancelAutosave();

  // Unsubscribe from history changes
  if (onEditHandler && Facade.isInitialized) {
    Facade.history.offChange(onEditHandler);
  }
  onEditHandler = null;

  if (onVisibilityChange) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    onVisibilityChange = null;
  }
  if (onPageHide) {
    window.removeEventListener("pagehide", onPageHide);
    onPageHide = null;
  }
  stopAutoSaveSettingWatch?.();
  stopAutoSaveSettingWatch = null;
  stopInteractionWatch?.();
  stopInteractionWatch = null;

  isHidden = false;
  isControllerInitialized = false;
  consecutiveFailures = 0;
  SaveState.value = "saved";
  Logger.info("[SaveController] Cleaned up");
}

/**
 * @deprecated Use saveDrawing() directly or initSaveController() in DrawingCanvas.vue
 */
export const useSaveDrawing = () => {
  return {
    save: saveDrawing,
    SaveState,
  };
};

/**
 * @deprecated The controller is now initialized in DrawingCanvas.vue.
 * SaveBadge should just import SaveState directly.
 */
export const useSaveDrawingController = () => {
  return {
    save: saveDrawing,
    SaveState,
  };
};
